import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Spacer from 'common/components/Spacer';
import areIntervalsOverlapping from 'date-fns/areIntervalsOverlapping';
import { getFormLabel } from 'common/entities/form/selectors';
import {
    getFormSessionStepsLabel,
    getFormSessionStepsNextButtonLabel,
    getFormSessionStepsSubmitButtonLabel,
} from 'common/entities/form/sessionRegistration';
import { useBootstrap } from 'utils/bootstrap/useBootstrap';

import FormNode from '../nodes/FormNode';

import FormLogo from './FormLogo';
import FormHeadline from './FormHeadline';
import FormSubmitButton from './FormSubmitButton';
import FormStepper from './FormStepper';
import FormBackButton from './FormBackButton';
import FormErrorContainer from '../errors/FormErrorContainer';
import { getFormSessionStep, getFormSessions } from '../../selectors';
import { FORM_SESSIONS_STEPS } from '../../constants/sessions';
import { submitSessionRegistrationFormEffect, validateGuestInformationEffect } from '../../epics/form';
import FormSessionListItem from './FormSessionListItem';
import { getFormSubmitting, getSelectedSessions } from '../../selectors/form';

const StyledButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
`;

const StyledFieldset = styled.fieldset`
    border: none;
    padding: 0;
    margin: 0;
    &:focus-visible {
        outline: none;
    }
`;

const SessionListItemContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-height: 20rem;
    overflow-y: auto;
    padding-right: 1rem;
`;

export const FormSessions = (props) => {
    const dispatch = useDispatch();

    const [openSessionDetailsId, setOpenSessionDetailsId] = useState(null);

    const sessions = useSelector(getFormSessions);
    const formStep = useSelector(getFormSessionStep);
    const formGuestInfoStepLabel = useSelector((state) => getFormLabel(state, props.formId));
    const formSessionStepLabel = useSelector((state) => getFormSessionStepsLabel(state, props.formId));
    const nextButtonLabel = useSelector((state) => getFormSessionStepsNextButtonLabel(state, props.formId));
    const submitButtonLabel = useSelector((state) => getFormSessionStepsSubmitButtonLabel(state, props.formId));
    const selectedSessions = useSelector(getSelectedSessions);
    const isFormSubmitting = useSelector(getFormSubmitting);
    const bootstrapData = useBootstrap(['event.preventSessionOverlap']);
    const preventSessionOverlap = bootstrapData['event.preventSessionOverlap'];

    const disabledSessions = useMemo(() => {
        if (!preventSessionOverlap) return {};

        const newDisabledSessions = {};
        const selectedSessionsList = sessions.filter((session) => !!selectedSessions[session.id]);

        sessions.forEach((session) => {
            const hasConflict = selectedSessionsList.some(
                (selectedSession) =>
                    selectedSession.id !== session.id &&
                    areIntervalsOverlapping(
                        { start: new Date(session.start), end: new Date(session.end) },
                        { start: new Date(selectedSession.start), end: new Date(selectedSession.end) },
                    ),
            );

            if (hasConflict && !selectedSessions[session.id]) {
                newDisabledSessions[session.id] = true;
            }
        });

        return newDisabledSessions;
    }, [selectedSessions, sessions, preventSessionOverlap]);

    const sessionGroupRef = useRef(null);

    useEffect(() => {
        if (formStep === FORM_SESSIONS_STEPS.SESSION_REGISTRATION) {
            if (sessionGroupRef.current) sessionGroupRef.current.focus();
        }
    }, [formStep]);

    const handleToggleExpand = (sessionId) => {
        setOpenSessionDetailsId(openSessionDetailsId !== sessionId ? sessionId : null);
    };

    if (formStep === FORM_SESSIONS_STEPS.SESSION_REGISTRATION) {
        return (
            <>
                <FormStepper />
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        dispatch(
                            submitSessionRegistrationFormEffect({
                                formId: props.formId,
                                isPreview: props.isPreview,
                            }),
                        );
                    }}
                    noValidate
                    aria-atomic
                >
                    <StyledFieldset aria-label={formSessionStepLabel} ref={sessionGroupRef} tabIndex={-1}>
                        <FormHeadline label={formSessionStepLabel} />
                        <SessionListItemContainer>
                            {sessions?.map((session) => (
                                <FormSessionListItem
                                    key={session.id}
                                    id={session.id}
                                    title={session.name}
                                    startTime={session.start}
                                    endTime={session.end}
                                    speakers={session.speakers}
                                    tags={session.tags}
                                    description={session.description}
                                    waitlist={session.waitlist}
                                    isExpanded={openSessionDetailsId === session.id}
                                    onToggleExpand={() => handleToggleExpand(session.id)}
                                    isSelected={!!selectedSessions[session.id]}
                                    isDisabled={disabledSessions[session.id]}
                                    status={session.status}
                                />
                            ))}
                        </SessionListItemContainer>
                    </StyledFieldset>
                    <Spacer $spacing="2.25rem" />
                    <StyledButtonContainer>
                        {!isFormSubmitting && <FormBackButton formId={props.formId} isPreview={props.isPreview} />}
                        <FormSubmitButton>{submitButtonLabel}</FormSubmitButton>
                    </StyledButtonContainer>
                </form>
            </>
        );
    }

    return (
        <>
            <FormStepper />
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    dispatch(
                        validateGuestInformationEffect({
                            formId: props.formId,
                            isPreview: props.isPreview,
                        }),
                    );
                }}
                noValidate
                aria-atomic
            >
                <FormLogo />
                <FormHeadline label={formGuestInfoStepLabel} />

                <div>
                    {props.formNodes.map((node) => (
                        <FormNode nodeId={node.id} groupId={props.groupId} key={node.id} />
                    ))}
                </div>

                <FormErrorContainer />
                <FormSubmitButton>{nextButtonLabel}</FormSubmitButton>
            </form>
        </>
    );
};

FormSessions.propTypes = {
    formNodes: PropTypes.array.isRequired,
    groupId: PropTypes.string.isRequired,
    formId: PropTypes.string.isRequired,
    isPreview: PropTypes.bool.isRequired,
};
